import { CSSProperties, ReactElement, useCallback, useEffect, useState } from 'react';
import { useLocation, Link, useParams } from 'react-router-dom';
import { cloneDeep } from 'lodash';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Divider, Grid, Typography } from '@mui/material';
import MuiBreadcrumbs from '@mui/material/Breadcrumbs';
import { RootState } from 'store';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
// project-imports
import MainCard from 'components/cards/MainCard';
// assets
import { Buildings2 } from 'iconsax-react';
// types
import { OverrideIcon } from 'types/root';
import { NavItemType } from 'types/menu';
import { useDispatch, useSelector } from 'react-redux';
import ConfirmationDialog from 'components/dialog/ConfirmationDialog';
import { useLogoutMutation } from 'api/auth';
import { resetDocumentElements } from 'store/slice/design/documentElementsSlice';
import { openDefaultSuccessSnackbar } from 'store/reducers/snackbar';
import { endNavigation } from 'store/reducers/navigationSlice';
import { setLocalStorageItems } from 'utils/fileUtils';
import useScreenSize from 'hooks/useScreenSize';
import UserQuickActions from 'components/UserQuickActions';
import { showPageBlocker } from 'store/reducers/pageBlockerSlice';
// ==============================|| BREADCRUMBS ||============================== //

export interface BreadCrumbSxProps extends CSSProperties {
  mb?: string;
  bgcolor?: string;
}

interface Props {
  card?: boolean;
  divider?: boolean;
  icon?: boolean;
  icons?: boolean;
  maxItems?: number;
  navigation?: { items: NavItemType[] };
  rightAlign?: boolean;
  separator?: OverrideIcon;
  title?: boolean;
  titleBottom?: boolean;
  sx?: BreadCrumbSxProps;
}

const Breadcrumbs = ({
  card,
  divider = true,
  icon,
  icons,
  maxItems,
  navigation,
  rightAlign,
  separator,
  title,
  titleBottom,
  sx,
  ...others
}: Props) => {
  const theme = useTheme();
  const location = useLocation();
  const dispatch = useDispatch();
  const [main, setMain] = useState<NavItemType | undefined>();
  const [item, setItem] = useState<NavItemType>();
  const [logout, { isLoading: logoutInProgress }] = useLogoutMutation();
  // const navigate = useNavigate();
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false);
  //const [forcePageBlock, setForcePageBlock] = useState<boolean>(false);
  const decodedAuthToken = useSelector((state: RootState) => state.auth.decodedAuthToken);
  const isFormDirty = useSelector((state: RootState) => state.formslice.isDirty);
  const isConfidenceModel = useSelector((state: RootState) => state.navigationSlice.navigating);
  const { processId, docTypeID } = useParams();
  const { isDesktop } = useScreenSize();
  const onLogoutConfirm = useCallback(async () => {
    try {
      if (decodedAuthToken) {
        setLocalStorageItems({
          user_id: decodedAuthToken.nameid,
          last_location: location.pathname
        });
      }
      await logout().unwrap();
      const lastCloseTime = localStorage.getItem('browserCloseTime');
      if (lastCloseTime) {
        localStorage.removeItem('browserCloseTime');
      }
      dispatch(resetDocumentElements());
      dispatch(
        openDefaultSuccessSnackbar({
          message: 'Your session is logged out successfully',
          preventAutoDismiss: true,
          hideDuration: 15000
        })
      );
    } catch (e) {}
  }, [dispatch, logout, location.pathname, decodedAuthToken]);

  const checkLogoutProcess = useCallback(() => {
    setShowLogoutConfirmation(false);
    if (isFormDirty) {
      dispatch(
        showPageBlocker({
          onConfirm: () => {
            onLogoutConfirm();
          },
          onCancel: () => {}
        })
      );
    } else {
      onLogoutConfirm();
    }
  }, [isFormDirty, onLogoutConfirm]);
  const iconSX = {
    marginRight: theme.spacing(0.75),
    marginTop: `-${theme.spacing(0.25)}`,
    width: '1rem',
    height: '1rem',
    color: theme.palette.secondary.main
  };
  // const handleChangePassword = useCallback(() => {
  //   navigate('/change-password');
  // }, [navigate]);
  useEffect(() => {
    navigation?.items?.map((menu: NavItemType, index: number) => {
      if (menu.type && menu.type === 'group') {
        getCollapse(menu as { children: NavItemType[]; type?: string });
      }
      return false;
    });
  });

  useEffect(() => {
    if (customLocation.includes('/change-password')) {
      setItem({ title: 'Change Password', type: 'item', breadcrumbs: true });
    }
    if (location.pathname.includes(`/design/document-settings/${processId}/${docTypeID}`)) {
      setItem({
        title: 'Document List',
        subTitle: 'DOCUMENT TYPE',
        type: 'item',
        breadcrumbs: true,
        url: `/design/document-settings/${processId}/${docTypeID}`
      });
    }
    if (location.pathname.includes(`design/file-configuration`)) {
      setItem({
        title: '',
        subTitle: '',
        type: 'item',
        breadcrumbs: false
      });
    }

    if (location.pathname.includes(`/design/data-point-configuration/${processId}/${docTypeID}`)) {
      setItem({
        title: 'Document List',
        subTitle: 'DOCUMENT TYPE',
        type: 'item',
        breadcrumbs: true,
        url: `/design/data-point-configuration/${processId}/${docTypeID}`
      });
    }
    if (location.pathname.includes(`/design/file-upload/${processId}/${docTypeID}`)) {
      setItem({
        title: 'Document List',
        subTitle: 'DOCUMENT TYPE',
        type: 'item',
        breadcrumbs: true,
        url: `/design/file-upload/${processId}/${docTypeID}`
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, navigation]);

  let customLocation = location.pathname;

  // only used for component demo breadcrumbs
  if (customLocation.includes('/components-overview/breadcrumbs')) {
    customLocation = '/apps/kanban/board';
  }

  if (customLocation.includes('/apps/kanban/backlogs')) {
    customLocation = '/apps/kanban/board';
  }

  useEffect(() => {
    if (customLocation.includes('/apps/profiles/user/payment')) {
      setItem(undefined);
    }
  }, [item, customLocation]);
  useEffect(() => {
    if (!location.pathname.includes('/design/confidence-model')) {
      dispatch(endNavigation());
    }
  }, [location.pathname, dispatch]);

  // set active item state
  const getCollapse = (menu: NavItemType) => {
    if (menu.children) {
      menu.children.filter((collapse: NavItemType) => {
        if (collapse.type && collapse.type === 'collapse') {
          getCollapse(collapse as { children: NavItemType[]; type?: string });
          if (collapse.url === customLocation) {
            setMain(collapse);
            setItem(collapse);
          }
        } else if (collapse.type && collapse.type === 'item') {
          if (customLocation === collapse.url) {
            setMain(menu);
            setItem(collapse);
          }
        }
        return false;
      });
    }
  };

  // item separator
  const separatorIcon = '/';

  let mainContent;
  let itemContent;
  let itemContent2;
  let breadcrumbContent: ReactElement | null = null;
  let itemTitle: NavItemType['title'] = '';
  let itemTitle2: NavItemType['subTitle'] = undefined;

  let CollapseIcon;
  let ItemIcon;

  // collapse item
  if (main && main.type === 'collapse' && main.breadcrumbs === true) {
    CollapseIcon = main.icon ? main.icon : Buildings2;
    mainContent = (
      <Typography
        component={Link}
        to={document.location.pathname}
        sx={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}
        color="secondary"
      >
        {icons && <CollapseIcon style={iconSX} />}
        {main.title}
      </Typography>
    );
    breadcrumbContent = (
      <MainCard
        border={card}
        sx={
          card === false
            ? { mb: 3, bgcolor: 'transparent', ...sx, backgroundImage: 'unset', boxShadow: 'none', overflow: 'visible' }
            : { mb: 3, ...sx, backgroundImage: 'unset', boxShadow: 'none', overflow: 'visible' }
        }
        // {...others}
        content={card}
        boxShadow={false}
      >
        <Grid
          container
          direction={rightAlign ? 'row' : 'column'}
          justifyContent={rightAlign ? 'space-between' : 'flex-start'}
          alignItems={rightAlign ? 'center' : 'flex-start'}
          spacing={0.5}
        >
          <Grid item>
            <MuiBreadcrumbs className="navBreadcrumbs" aria-label="breadcrumb" maxItems={maxItems || 8} separator={separatorIcon}>
              <Typography
                // component={Link}
                // to="/"

                sx={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}
                color="textPrimary"
              >
                {/* {icons && <Home3 style={iconSX} />}
                {icon && !icons && <Home3 variant="Bold" style={{ ...iconSX, marginRight: 0 }} />}
                {(!icon || icons) && 'Home'} */}
                {(item?.url && item?.url?.split('/')[1]?.toUpperCase()) || 'Home'}
              </Typography>
              {mainContent}
            </MuiBreadcrumbs>
          </Grid>
          {title && titleBottom && (
            <Grid item sx={{ mt: card === false ? 0 : 1 }}>
              <Typography variant="h2" className="titleBreadcrumbs" sx={{ fontWeight: 400 }}>
                {main.title}
              </Typography>
            </Grid>
          )}
        </Grid>
        {card === false && divider !== false && <Divider sx={{ mt: 2 }} />}
        <ConfirmationDialog
          open={showLogoutConfirmation}
          onClose={() => setShowLogoutConfirmation(false)}
          onConfirm={checkLogoutProcess}
          isLoading={logoutInProgress}
          message="Are you sure to logoff?"
          icon={<WarningAmberIcon style={{ color: '#FF0000' }} sx={{ fontSize: 50, mb: 2 }} />}
          confirmButtonLabel="Yes"
          cancelButtonLabel="No"
          actionType="logout"
        />
      </MainCard>
    );
  }
  let clonedItem = cloneDeep(item);

  switch (true) {
    case location.pathname.includes(`/design/confidence-model`):
      if (clonedItem) {
        if (isConfidenceModel) {
          clonedItem.subTitle = 'CONFIDENCE MODEL';
          clonedItem.pageHeader = 'Confidence Field';
        }
      }
      break;

    case location.pathname.includes(`/design/data-point-configuration/${processId}/${docTypeID}`):
      if (clonedItem) {
        clonedItem.pageHeader = 'Data Point Configuration';
        clonedItem.subTitle = undefined; // Reset subTitle
      }
      break;

    case location.pathname.includes(`/design/file-upload/${processId}/${docTypeID}`):
      if (clonedItem) {
        clonedItem.pageHeader = 'File Upload';
        clonedItem.subTitle = undefined; // Reset subTitle
      }
      break;
    case location.pathname.includes(`/design/document-settings/${processId}/${docTypeID}`):
      if (clonedItem) {
        clonedItem.pageHeader = 'Document Settings';
        clonedItem.subTitle = undefined; // Reset subTitle
      }
      break;

    default:
      if (clonedItem) {
        clonedItem.subTitle = undefined;
        clonedItem.pageHeader = undefined; // Set both to undefined
      }
      break;
  }

  // items
  if (item && clonedItem && item.type === 'item') {
    if (typeof item?.title === 'string') {
      itemTitle = item.title.toUpperCase();
    } else {
      itemTitle = item?.title;
    }

    ItemIcon = item.icon ? item.icon : Buildings2;
    itemContent = (
      <Typography className="breadcrumbsTitleText" color="textPrimary" sx={{ display: 'flex', alignItems: 'center' }}>
        {icons && <ItemIcon style={iconSX} />}
        {itemTitle}
      </Typography>
    );
    if (item.subTitle) {
      itemTitle2 = item.subTitle?.toUpperCase();
      itemContent2 = (
        <Typography className="breadcrumbsTitleText" color="textPrimary" sx={{ display: 'flex', alignItems: 'center' }}>
          {icons && <ItemIcon style={iconSX} />}
          {itemTitle2}
        </Typography>
      );
    }
    if (clonedItem.subTitle) {
      itemTitle2 = clonedItem.subTitle?.toUpperCase();
      itemContent2 = (
        <Typography className="breadcrumbsTitleText" color="textPrimary" sx={{ display: 'flex', alignItems: 'center' }}>
          {icons && <ItemIcon style={iconSX} />}
          {itemTitle2}
        </Typography>
      );
    }

    // main
    if (item.breadcrumbs !== false) {
      breadcrumbContent = (
        <MainCard
          border={card}
          sx={
            card === false
              ? { mb: 3, bgcolor: 'transparent', ...sx, backgroundImage: 'unset', boxShadow: 'none', overflow: 'visible' }
              : { mb: 3, ...sx, backgroundImage: 'unset', boxShadow: 'none', overflow: 'visible' }
          }
          {...others}
          content={card}
          boxShadow={false}
          className="mainBreadcrumbs"
        >
          <Grid item xs={12} container display={'flex'} justifyContent={'space-between'} alignItems={'center'} alignContent={'center'}>
            <Grid item xs={isDesktop ? 6 : 12}>
              <Grid
                item
                container
                direction={rightAlign ? 'row' : 'column'}
                justifyContent={rightAlign ? 'space-between' : 'flex-start'}
                alignItems={rightAlign ? 'center' : 'flex-start'}
                spacing={0.5}
              >
                {title && !titleBottom && (
                  <Grid item>
                    <Typography variant="h2" className="titleBreadcrumbs" sx={{ fontWeight: 400 }}>
                      {clonedItem.pageHeader ? clonedItem.pageHeader : item?.title}
                    </Typography>
                  </Grid>
                )}
                <Grid item>
                  <>
                    <MuiBreadcrumbs aria-label="breadcrumb" maxItems={maxItems || 8} separator={separatorIcon}>
                      {item?.title === 'Change Password' ? (
                        <Typography
                          color="textPrimary"
                          className="breadcrumbsTitleText"
                          // sx={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}
                        >
                          USER PROFILE
                        </Typography>
                      ) : (
                        <Typography
                          // component={Link}
                          // to="/"
                          className="breadcrumbsTitleText"
                          color="textPrimary"

                          // sx={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}
                        >
                          {(item?.url && item?.url.split('/')[1])?.toUpperCase() === 'REPORT'
                            ? 'REPORTS'
                            : item?.url && item?.url.split('/')[1]?.toUpperCase()}
                        </Typography>
                      )}

                      {mainContent}
                      {item?.url && item?.url.split('/').length > 3 && item?.title !== 'Change Password' && itemContent}
                      {itemContent2}
                    </MuiBreadcrumbs>
                  </>
                </Grid>
                {title && titleBottom && (
                  <Grid item sx={{ mt: card === false ? 0 : 1 }}>
                    <Typography variant="h2" className="titleBreadcrumbs" sx={{ fontWeight: 400 }}>
                      {item?.title}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>
            {isDesktop && (
              <Grid item xs={6} display={'flex'} justifyContent={'flex-end'} alignContent={'center'} alignItems={'center'}>
                <UserQuickActions
                  showLogoutConfirmation={showLogoutConfirmation}
                  setShowLogoutConfirmation={setShowLogoutConfirmation}
                  checkLogoutProcess={checkLogoutProcess}
                  logoutInProgress={logoutInProgress}
                />
              </Grid>
            )}
          </Grid>
          {card === false && divider !== false && <Divider sx={{ mt: 2 }} />}
        </MainCard>
      );
    }
  }

  if (breadcrumbContent === null) {
  }

  return breadcrumbContent;
};

export default Breadcrumbs;
